import React, { useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import 'leaflet/dist/leaflet.css';
import useTranslation from '../../customHooks/translations';
import { indexByName, styleForFeature, tooltipLabel, FALLBACK_FILL } from './mushroomRadarStyle';

// Rough bounds of Poland [[south, west], [north, east]].
const POLAND_BOUNDS = [[49.0, 14.1], [54.9, 24.2]];

// grzyby.pl's own radar hues, low → high occurrence, for the gradient legend.
const SCALE_STOPS = ['#84de3a', '#d7f113', '#fdd50b', '#ffa400', '#f25a01'];

function MushroomRadarLegend() {
  const t = useTranslation();
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ display: 'block', fontWeight: 600, color: 'text.primary' }}
      >
        {t.mushroomRadar.legendTitle}
      </Typography>
      <Box
        sx={{
          mt: 0.75,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 220px', minWidth: 200 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {t.mushroomRadar.legendLow}
          </Typography>
          <Box
            aria-hidden
            sx={{
              flex: 1,
              height: 12,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(to right, ${SCALE_STOPS.join(', ')})`,
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {t.mushroomRadar.legendHigh}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            aria-hidden
            sx={{
              width: 14,
              height: 14,
              borderRadius: 0.5,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: FALLBACK_FILL,
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {t.mushroomRadar.legendNoData}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function MushroomRadarMap({ regions, scrapedAt }) {
  const t = useTranslation();
  const [geo, setGeo] = React.useState(null);
  const [geoError, setGeoError] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    fetch(`${process.env.PUBLIC_URL || ''}/geo/poland-voivodeships.geojson`)
      .then((r) => { if (!r.ok) throw new Error('geo'); return r.json(); })
      .then((j) => { if (alive) setGeo(j); })
      .catch(() => { if (alive) setGeoError(true); });
    return () => { alive = false; };
  }, []);

  const index = useMemo(() => indexByName(regions), [regions]);
  const styleFn = useMemo(() => styleForFeature(index), [index]);

  const onEachFeature = React.useCallback((feature, layer) => {
    const props = feature.properties || {};
    const key = (props.nazwa || props.name || '').toLowerCase().trim();
    const region = index[key];
    layer.bindTooltip(region ? tooltipLabel(region) : (props.nazwa || props.name || ''), { sticky: true });
  }, [index]);

  if (geoError) return <Typography sx={{ p: 2 }}>{t.mushroomRadar.error}</Typography>;

  return (
    <Box sx={{ position: 'relative' }}>
      <MapContainer
        bounds={POLAND_BOUNDS}
        style={{ height: '60vh', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {geo && (
          <GeoJSON
            key={scrapedAt || 'radar'}
            data={geo}
            style={styleFn}
            onEachFeature={onEachFeature}
          />
        )}
      </MapContainer>
      <MushroomRadarLegend />
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
        {t.mushroomRadar.legendNote}
      </Typography>
    </Box>
  );
}
